/**
 * Key Ingredients Component
 * Displays key ingredients with images and an accordion for full ingredient list
 */

'use client';

import styled from '@emotion/styled';
import { Text, Accordion, useTheme } from '@template-project/afrodita-components';
import { ProductIngredient } from '@/domain/entities/product.entity';
import { useScrollReveal } from '@/lib/hooks/use-scroll-reveal';
 

export interface KeyIngredientsProps {
  title?: string;
  ingredients: ProductIngredient[];
}

interface RevealProps {
  $visible: boolean;
}

const RevealWrapper = styled.div<RevealProps>`
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transform: ${({ $visible }) => ($visible ? 'translateY(0)' : 'translateY(20px)')};
  transition: opacity 0.35s ease, transform 0.35s ease;

  @media (prefers-reduced-motion: reduce) {
    opacity: 1;
    transform: none;
    transition: none;
  }
`;

const Container = styled.section`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: ${({ theme }) => `${theme.space['2xl']} ${theme.space.md}`};
  background-color: ${({ theme }) => theme.colors.background.primary};
`;

const Title = styled.h2`
  font-size: ${({ theme }) => theme.typography.variants.heading2.fontSize}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.demi};
  text-align: center;
  margin: ${({ theme }) => `0 0 ${theme.space['2xl']}`};
  color: ${({ theme }) => theme.colors.text.primary};

  @media (max-width: ${({ theme }) => theme.mediaQueries.md}) {
    font-size: ${({ theme }) => theme.typography.variants.heading4.fontSize}px;
  }
`;

const IngredientWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${({ theme }) => theme.space['2xl']};
  margin-bottom: ${({ theme }) => theme.space['2xl']};

  @media (max-width: ${({ theme }) => theme.mediaQueries.md}) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.space.xl};
  }
`;

const Ingredient = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.md};
  text-align: center;
  align-items: center;
`;

const ImageStyled = styled.img`
  width: 120px;
  height: 120px;
  object-fit: cover;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.background.secondary};
`;

const IngredientDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.sm};
`;

const IngredientName = styled.h3`
  font-weight: ${({ theme }) => theme.typography.config.weight.demi};
  font-size: ${({ theme }) => theme.fontSizes[4]}px;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const IngredientDescription = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  line-height: 1.6;
`;

const AccordionWrapper = styled.div`
  margin-top: ${({ theme }) => theme.space.xl};
  border-top: 1px solid ${({ theme }) => theme.colors.border.color};
  padding-top: ${({ theme }) => theme.space.xl};
`;

const FullIngredientsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.lg};
  padding: ${({ theme }) => `${theme.space.md} 0`};
`;

const IngredientItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.sm};
`;

const PlusIcon = styled.span`
  display: inline-block;
  width: 20px;
  height: 20px;
  position: relative;
  
  &::before,
  &::after {
    content: '';
    position: absolute;
    background-color: currentColor;
  }
  
  &::before {
    width: 20px;
    height: 2px;
    top: 9px;
    left: 0;
  }
  
  &::after {
    width: 2px;
    height: 20px;
    top: 0;
    left: 9px;
  }
`;

const MinusIcon = styled.span`
  display: inline-block;
  width: 20px;
  height: 2px;
  background-color: currentColor;
`;

export function KeyIngredients({
  title = 'Key Ingredients',
  ingredients,
}: KeyIngredientsProps) {
  const theme = useTheme();
  const { ref, isVisible } = useScrollReveal();

  if (!ingredients || ingredients.length === 0) {
    return null;
  }

  // Separate key ingredients (with images) from full ingredient list (for accordion)
  const keyIngredients = ingredients.filter(
    (ing) => ing.title !== 'full_ingredient_accordion' && ing.title !== 'full_ingredient'
  );
  const fullIngredientsList = ingredients.filter(
    (ing) => ing.title === 'full_ingredient_accordion' || ing.title === 'full_ingredient'
  );

  return (
    <Container>
      <RevealWrapper ref={ref} $visible={isVisible}>
      <Title>{title}</Title>
      
      {keyIngredients.length > 0 && (
        <IngredientWrapper>
          {keyIngredients.map((ingredient) => (
            <Ingredient key={ingredient.id}>
              {ingredient.image && (
                <ImageStyled
                  alt={`${ingredient.name} ingredient`}
                  src={ingredient.image}
                />
              )}
              <IngredientDetails>
                <IngredientName>{ingredient.name}</IngredientName>
                <IngredientDescription>
                  {ingredient.description}
                </IngredientDescription>
              </IngredientDetails>
            </Ingredient>
          ))}
        </IngredientWrapper>
      )}

      {fullIngredientsList.length > 0 && (
        <AccordionWrapper>
          <Accordion
            closeIcon={MinusIcon}
            content={
              <FullIngredientsList>
                {fullIngredientsList.map((ingredient) => (
                  <IngredientItem key={ingredient.id}>
                    <Text
                      size="regular"
                      variant="body"
                      weight="bold"
                    >
                      {ingredient.name}
                    </Text>
                    <Text size="small" variant="body">
                      {ingredient.description}
                    </Text>
                  </IngredientItem>
                ))}
              </FullIngredientsList>
            }
            header={
              <Text variant="body" weight="bold">
                Full Ingredients List
              </Text>
            }
            openIcon={PlusIcon}
            variant="simple"
          />
        </AccordionWrapper>
      )}
      </RevealWrapper>
    </Container>
  );
}

